import Drawer from "common/Drawers/Drawer";
import React, { useContext, useEffect } from "react";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import Title from "common/Typography/Title";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import Flex from "common/Boxes/Flex";
import DemoTag from "common/Tags/DemoTag";
import CreateAccountContent from "features/portfolio/CreateAccount/CreateAccountContent";
import { sharedLib } from "@shared/services/shared.service";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import useQuery from "utils/hooks/useQuery";
import styled from "styled-components";
import useMedia from "utils/hooks/useMedia";
import {
  CREATE_ACCOUNT_PATH,
  INTERCOM_TOTAL_SIZE,
  LARGE_MOBILE,
} from "common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { AddClientResponse } from "@shared/types";
import { useClientsData } from "@shared/utils/hooks/misc";
import useCreateAccountMessage from "features/configuration/accounts/CreateAccount/useCreateAccountMessage";
import useFormattedNotification from "utils/hooks/useFormattedNotification";
import QuickConnectProvider from "features/configuration/accounts/CreateAccount/QuickConnectProvider";
import { useBottomNavigator } from "utils/hooks/misc";
import { matchPath, useLocation } from "react-router-dom";

const CreateAccountDrawer = () => {
  const location = useLocation();
  const { createAccountDrawerOpen, closeCreateAccountDrawer } =
    useContext(AccountFormContext)!;
  const { getSuccessMessage, contextHolder: successContextHolder } =
    useCreateAccountMessage();
  const { getErrorNotification, contextHolder: errorContextHolder } =
    useFormattedNotification();

  useEffect(() => {
    if (!matchPath(location.pathname, { path: CREATE_ACCOUNT_PATH })) {
      closeCreateAccountDrawer();
    }
  }, [location.pathname]);

  return (
    <QuickConnectProvider
      getSuccessMessage={getSuccessMessage}
      getErrorNotification={getErrorNotification}
    >
      <CreateAccountDrawerContent
        open={createAccountDrawerOpen}
        getErrorNotification={getErrorNotification}
        getSuccessMessage={getSuccessMessage}
      />
      {successContextHolder}
      {errorContextHolder}
    </QuickConnectProvider>
  );
};

interface CreateAccountDrawerProps {
  open?: boolean;
  getErrorNotification?: (description: string, title?: string) => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const CreateAccountDrawerContent = ({
  open,
  getErrorNotification,
  getSuccessMessage,
}: CreateAccountDrawerProps) => {
  const isLargeMobile = useMedia(
    [`(max-width: ${LARGE_MOBILE})`],
    [true],
    false
  );
  const { closeDrawer, quickConnectLoading } = useContext(QuickConnectContext)!;
  const { isDemoWorkspace, getAccountSubExchange, tab } =
    useContext(AccountFormContext)!;
  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();
  const bottomNavigatorData = useBottomNavigator();
  const query = useQuery();
  const queryExchange = query.get("exchange") || undefined;
  const accountId = query.get("account") || undefined;
  const { getAccountById } = useClientsData();
  const account = getAccountById(Number(accountId));
  const accountSubExchange = getAccountSubExchange(account);
  const primaryExchange = queryExchange ?? accountSubExchange?.nested_exchange;

  const title = (
    <Flex style={{ marginBottom: 32, justifyContent: "center" }}>
      <Title level={3}>{account ? "Edit" : "Create"} Account</Title>
      {isDemoWorkspace && <DemoTag isSmall={true} style={{ marginLeft: 8 }} />}
    </Flex>
  );

  const mainContent = (
    <ContentWrapper
      $isLargeMobile={isLargeMobile}
      $isBottomNavigator={bottomNavigatorData.baseShouldDisplay}
    >
      <LoadingSpinnerOverlay
        isLoading={quickConnectLoading}
        hideContentWhileLoading={!primaryExchange}
        spinnerMarginTop={250}
        wrap={true}
        wrapStyle={
          bottomNavigatorData.baseShouldDisplay
            ? { height: "100%", display: "flex", flexDirection: "column" }
            : undefined
        }
      >
        {title}
        <CreateAccountContent
          exchange={primaryExchange}
          onSubmitSuccess={() => getPortfolioData({ use_cache: false })}
          getErrorNotification={getErrorNotification}
          account={account}
          getSuccessMessage={getSuccessMessage}
        />
        {!bottomNavigatorData.baseShouldDisplay && (
          <div style={{ height: INTERCOM_TOTAL_SIZE }} />
        )}
      </LoadingSpinnerOverlay>
    </ContentWrapper>
  );

  const showExtension = !isLargeMobile && !!primaryExchange;
  const extension = showExtension && (
    <ExtensionWrapper>
      <ConnectExchangeGuide
        primaryExchange={primaryExchange}
        selectedSubExchange={accountSubExchange}
        isQuickConnect={tab === ConnectExchangeTabs.QUICK_CONNECT}
      />
    </ExtensionWrapper>
  );

  return (
    <StyledDrawer
      open={open}
      onClose={closeDrawer}
      styles={{
        body: {
          display: "flex",
          height: "100%",
          overflow: "unset",
        },
        content: { overflow: "unset" },
      }}
      width={showExtension ? CONTENT_WIDTH + EXTENSION_WIDTH : CONTENT_WIDTH}
    >
      {extension}
      {mainContent}
    </StyledDrawer>
  );
};

export default React.memo(CreateAccountDrawer);

const EXTENSION_WIDTH = 450;
const CONTENT_WIDTH = 660;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0 !important;
  }
`;
const ContentWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $isLargeMobile?: boolean;
}>`
  max-width: ${CONTENT_WIDTH}px;
  padding: ${(p) => {
    if (p.$isBottomNavigator) return "12px 0";
    return p.$isLargeMobile ? "12px 16px 68px" : "12px 24px 68px";
  }};
  height: 100%;
  width: 100%;
  margin-bottom: 60px;
  overflow: ${(p) => (p.$isBottomNavigator ? "hidden" : "auto")};
`;
const ExtensionWrapper = styled.div`
  max-width: ${EXTENSION_WIDTH}px;
  height: 100%;
  padding: 40px 24px 24px;
  background: ${(p) => p.theme.colors.statisticsBgColor};
  overflow-y: unset;
`;
